<template>
  <AdminLayout page-title="AI Agents" page-subtitle="Manage AI assistants and automation">
    <div class="space-y-8">
      <!-- AI Agents Overview -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
        <!-- Active Agents Card -->
        <div class="admin-stat-card group">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Active Agents</p>
              <p class="text-3xl font-bold text-white">{{ agentStats.activeAgents }}</p>
              <p class="text-sm text-emerald-400 mt-3">
                <span class="inline-flex items-center">
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                  </svg>
                  {{ agentStats.responseRate }}% response rate
                </span>
              </p>
            </div>
            <div class="w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-2xl flex items-center justify-center">
              <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
            </div>
          </div>
        </div>

        <!-- Total Tasks Card -->
        <div class="admin-stat-card group">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Tasks Completed</p>
              <p class="text-3xl font-bold text-white">{{ agentStats.totalTasks.toLocaleString() }}</p>
              <p class="text-sm text-emerald-400 mt-3">
                <span class="inline-flex items-center">
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                  </svg>
                  +{{ agentStats.dailyTasks }} today
                </span>
              </p>
            </div>
            <div class="w-12 h-12 bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-2xl flex items-center justify-center">
              <svg class="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <!-- Efficiency Score Card -->
        <div class="admin-stat-card group">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Efficiency Score</p>
              <p class="text-3xl font-bold text-white">{{ agentStats.efficiencyScore }}%</p>
              <p class="text-sm text-emerald-400 mt-3">
                <span class="inline-flex items-center">
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                  </svg>
                  +{{ agentStats.efficiencyImprovement }}% this month
                </span>
              </p>
            </div>
            <div class="w-12 h-12 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-2xl flex items-center justify-center">
              <svg class="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7"/>
              </svg>
            </div>
          </div>
        </div>

        <!-- Cost Savings Card -->
        <div class="admin-stat-card group">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Cost Savings</p>
              <p class="text-3xl font-bold text-white">${{ agentStats.costSavings.toLocaleString() }}</p>
              <p class="text-sm text-emerald-400 mt-3">
                <span class="inline-flex items-center">
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                  </svg>
                  ${{ agentStats.monthlySavings.toLocaleString() }} this month
                </span>
              </p>
            </div>
            <div class="w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-2xl flex items-center justify-center">
              <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="admin-card">
        <div class="admin-card-header">
          <div class="flex items-center justify-between">
            <div>
              <h2 class="admin-card-title">Quick Actions</h2>
              <p class="admin-card-subtitle">Common AI agent management tasks</p>
            </div>
          </div>
        </div>

        <div class="admin-card-body">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button @click="createNewAgent" class="quick-action-card group">
              <div class="quick-action-icon bg-gradient-to-br from-emerald-500 to-cyan-500">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                </svg>
              </div>
              <h3 class="text-sm font-semibold text-white mb-1">Create Agent</h3>
              <p class="text-xs text-slate-400">Deploy new AI assistant</p>
            </button>

            <button @click="viewAnalytics" class="quick-action-card group">
              <div class="quick-action-icon bg-gradient-to-br from-purple-500 to-pink-500">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                </svg>
              </div>
              <h3 class="text-sm font-semibold text-white mb-1">View Analytics</h3>
              <p class="text-xs text-slate-400">Performance insights</p>
            </button>

            <button @click="configureSettings" class="quick-action-card group">
              <div class="quick-action-icon bg-gradient-to-br from-yellow-500 to-orange-500">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
              </div>
              <h3 class="text-sm font-semibold text-white mb-1">Configure</h3>
              <p class="text-xs text-slate-400">Global settings</p>
            </button>

            <button @click="viewLogs" class="quick-action-card group">
              <div class="quick-action-icon bg-gradient-to-br from-blue-500 to-cyan-500">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <h3 class="text-sm font-semibold text-white mb-1">View Logs</h3>
              <p class="text-xs text-slate-400">Activity history</p>
            </button>
          </div>
        </div>
      </div>

      <!-- AI Agents Management -->
      <div class="admin-card">
        <div class="admin-card-header">
          <div class="flex items-center justify-between">
            <div>
              <h2 class="admin-card-title">AI Agents</h2>
              <p class="admin-card-subtitle">Manage your AI assistants and their capabilities</p>
            </div>
            <div class="flex items-center space-x-3">
              <select v-model="statusFilter" class="form-select">
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="training">Training</option>
              </select>
              <button @click="createNewAgent" class="btn-hero-primary">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                </svg>
                Create Agent
              </button>
            </div>
          </div>
        </div>

        <div class="admin-card-body">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div v-for="agent in filteredAgents" :key="agent.id" class="agent-card">
              <div class="agent-header">
                <div class="flex items-start justify-between mb-4">
                  <div class="agent-icon" :class="getAgentIconClass(agent.category)">
                    <component :is="getAgentIcon(agent.category)" class="w-6 h-6 text-white" />
                  </div>
                  <span :class="getStatusBadgeClass(agent.status)" class="px-2 py-1 rounded-full text-xs font-medium">
                    {{ agent.status }}
                  </span>
                </div>
                
                <h3 class="agent-name">{{ agent.name }}</h3>
                <p class="agent-category">{{ agent.category }}</p>
              </div>

              <div class="agent-body">
                <p class="agent-description">{{ agent.description }}</p>
                
                <div class="agent-metrics">
                  <div class="metric-item">
                    <span class="metric-label">Tasks</span>
                    <span class="metric-value">{{ agent.tasksCompleted.toLocaleString() }}</span>
                  </div>
                  <div class="metric-item">
                    <span class="metric-label">Success Rate</span>
                    <span class="metric-value">{{ agent.successRate }}%</span>
                  </div>
                  <div class="metric-item">
                    <span class="metric-label">Efficiency</span>
                    <span class="metric-value">{{ agent.efficiency }}%</span>
                  </div>
                </div>

                <div class="agent-capabilities">
                  <h4 class="capabilities-title">Capabilities</h4>
                  <div class="capabilities-list">
                    <span v-for="capability in agent.capabilities" :key="capability" class="capability-tag">
                      {{ capability }}
                    </span>
                  </div>
                </div>

                <div class="agent-actions">
                  <button @click="toggleAgent(agent.id)" :class="agent.status === 'active' ? 'btn-warning' : 'btn-success'" class="btn-sm">
                    {{ agent.status === 'active' ? 'Pause' : 'Activate' }}
                  </button>
                  <button @click="configureAgent(agent.id)" class="btn-secondary btn-sm">
                    Configure
                  </button>
                  <button @click="viewAgentStats(agent.id)" class="btn-primary btn-sm">
                    Stats
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="filteredAgents.length === 0" class="empty-state">
            <svg class="w-16 h-16 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            <h3 class="text-lg font-medium text-white mb-2">No Agents Found</h3>
            <p class="text-slate-400 mb-6">Create your first AI agent to start automating tasks</p>
            <button @click="createNewAgent" class="btn-hero-primary">
              Create Your First Agent
            </button>
          </div>
        </div>
      </div>

      <!-- Create Agent Modal -->
      <div v-if="showCreateModal" class="modal-overlay" @click="closeCreateModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3 class="modal-title">Create New AI Agent</h3>
            <button @click="closeCreateModal" class="modal-close">
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>
          </div>

          <form @submit.prevent="saveAgent" class="modal-body">
            <div class="form-grid">
              <div class="form-group">
                <label class="form-label">Agent Name *</label>
                <input v-model="agentForm.name" type="text" required class="form-input" placeholder="e.g., Safety Compliance Assistant" />
              </div>

              <div class="form-group">
                <label class="form-label">Category *</label>
                <select v-model="agentForm.category" required class="form-select">
                  <option value="">Select Category</option>
                  <option value="job-matching">Job Matching</option>
                  <option value="safety-compliance">Safety & Compliance</option>
                  <option value="schedule-optimization">Schedule Optimization</option>
                  <option value="document-processing">Document Processing</option>
                  <option value="data-analysis">Data Analysis</option>
                  <option value="communication">Communication</option>
                </select>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">Description</label>
              <textarea v-model="agentForm.description" rows="3" class="form-input" placeholder="Describe what this agent does and how it helps..."></textarea>
            </div>

            <div class="form-group">
              <label class="form-label">Capabilities</label>
              <div class="capabilities-input">
                <input v-model="newCapability" @keyup.enter="addCapability" type="text" class="form-input" placeholder="Add capability and press Enter" />
                <div class="capabilities-tags">
                  <span v-for="(capability, index) in agentForm.capabilities" :key="index" class="capability-tag-editable">
                    {{ capability }}
                    <button type="button" @click="removeCapability(index)" class="capability-remove">×</button>
                  </span>
                </div>
              </div>
            </div>

            <div class="modal-actions">
              <button type="button" @click="closeCreateModal" class="btn-secondary">Cancel</button>
              <button type="submit" class="btn-hero-primary">Create Agent</button>
            </div>
          </form>
        </div>
      </div>

      <!-- Configure Agent Modal -->
      <div v-if="showConfigModal" class="modal-overlay" @click="closeConfigModal">
        <div class="modal-content modal-large" @click.stop>
          <div class="modal-header">
            <h3 class="modal-title">Configure Agent: {{ configuringAgent?.name }}</h3>
            <button @click="closeConfigModal" class="modal-close">
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>
          </div>

          <form @submit.prevent="saveAgentConfiguration" class="modal-body">
            <div class="config-sections">
              <!-- Performance Settings -->
              <div class="config-section">
                <h4 class="config-section-title">Performance Settings</h4>
                <div class="form-grid">
                  <div class="form-group">
                    <label class="form-label">Max Tasks Per Hour</label>
                    <input v-model.number="agentConfig.maxTasksPerHour" type="number" min="1" max="1000" class="form-input" />
                    <p class="form-hint">Maximum number of tasks this agent can process per hour</p>
                  </div>

                  <div class="form-group">
                    <label class="form-label">Priority Level</label>
                    <select v-model="agentConfig.priorityLevel" class="form-select">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                    <p class="form-hint">Task processing priority relative to other agents</p>
                  </div>
                </div>
              </div>

              <!-- Reliability Settings -->
              <div class="config-section">
                <h4 class="config-section-title">Reliability Settings</h4>
                <div class="space-y-6">
                  <div class="form-group">
                    <div class="form-checkbox">
                      <label>
                        <input v-model="agentConfig.autoRetry" type="checkbox" />
                        <span class="checkbox-label">Enable Auto Retry</span>
                      </label>
                    </div>
                    <p class="form-hint">Automatically retry failed tasks</p>
                  </div>

                  <div class="form-group" v-if="agentConfig.autoRetry">
                    <label class="form-label">Retry Attempts</label>
                    <input v-model.number="agentConfig.retryAttempts" type="number" min="1" max="10" class="form-input" />
                    <p class="form-hint">Number of retry attempts for failed tasks</p>
                  </div>
                </div>
              </div>

              <!-- Monitoring Settings -->
              <div class="config-section">
                <h4 class="config-section-title">Monitoring & Notifications</h4>
                <div class="form-grid">
                  <div class="form-group">
                    <div class="form-checkbox">
                      <label>
                        <input v-model="agentConfig.notificationEnabled" type="checkbox" />
                        <span class="checkbox-label">Enable Notifications</span>
                      </label>
                    </div>
                    <p class="form-hint">Receive notifications for agent status changes</p>
                  </div>

                  <div class="form-group">
                    <label class="form-label">Logging Level</label>
                    <select v-model="agentConfig.loggingLevel" class="form-select">
                      <option value="basic">Basic</option>
                      <option value="detailed">Detailed</option>
                      <option value="verbose">Verbose</option>
                    </select>
                    <p class="form-hint">Level of detail for agent activity logs</p>
                  </div>
                </div>
              </div>

              <!-- Operating Hours -->
              <div class="config-section">
                <h4 class="config-section-title">Operating Hours</h4>
                
                <div class="form-group">
                  <div class="form-checkbox">
                    <label>
                      <input v-model="agentConfig.operatingHours.enabled" type="checkbox" />
                      <span class="checkbox-label">Restrict Operating Hours</span>
                    </label>
                  </div>
                  <p class="form-hint">Limit agent activity to specific hours</p>
                </div>

                <div v-if="agentConfig.operatingHours.enabled" class="mt-8 space-y-6">
                  <div class="form-group">
                    <label class="form-label">Start Time</label>
                    <input v-model="agentConfig.operatingHours.startTime" type="time" class="form-input" />
                  </div>

                  <div class="form-group">
                    <label class="form-label">End Time</label>
                    <input v-model="agentConfig.operatingHours.endTime" type="time" class="form-input" />
                  </div>
                </div>
              </div>

              <!-- Resource Limits -->
              <div class="config-section">
                <h4 class="config-section-title">Resource Limits</h4>
                <div class="form-grid">
                  <div class="form-group">
                    <label class="form-label">CPU Limit (%)</label>
                    <input v-model.number="agentConfig.resourceLimits.cpuLimit" type="number" min="10" max="100" class="form-input" />
                    <p class="form-hint">Maximum CPU usage percentage</p>
                  </div>

                  <div class="form-group">
                    <label class="form-label">Memory Limit (MB)</label>
                    <input v-model.number="agentConfig.resourceLimits.memoryLimit" type="number" min="128" max="8192" class="form-input" />
                    <p class="form-hint">Maximum memory usage in megabytes</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="modal-actions">
              <button type="button" @click="closeConfigModal" class="btn-secondary">Cancel</button>
              <button type="submit" class="btn-hero-primary">Save Configuration</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { computed, h, onMounted, ref } from 'vue'
import AdminLayout from './AdminLayout.vue'

interface AIAgent {
  id: string
  name: string
  description: string
  category: string
  status: 'active' | 'inactive' | 'training'
  tasksCompleted: number
  successRate: number
  efficiency: number
  capabilities: string[]
  created_at: string
  updated_at: string
}

interface AgentForm {
  name: string
  category: string
  description: string
  capabilities: string[]
}

interface AgentConfig {
  maxTasksPerHour: number
  priorityLevel: 'low' | 'medium' | 'high' | 'critical'
  autoRetry: boolean
  retryAttempts: number
  notificationEnabled: boolean
  loggingLevel: 'basic' | 'detailed' | 'verbose'
  operatingHours: {
    enabled: boolean
    startTime: string
    endTime: string
  }
  resourceLimits: {
    cpuLimit: number
    memoryLimit: number
  }
}

// State
const aiAgents = ref<AIAgent[]>([])
const statusFilter = ref('')
const showCreateModal = ref(false)
const showConfigModal = ref(false)
const newCapability = ref('')
const configuringAgent = ref<AIAgent | null>(null)

const agentForm = ref<AgentForm>({
  name: '',
  category: '',
  description: '',
  capabilities: []
})

const agentConfig = ref<AgentConfig>({
  maxTasksPerHour: 100,
  priorityLevel: 'medium',
  autoRetry: true,
  retryAttempts: 3,
  notificationEnabled: true,
  loggingLevel: 'detailed',
  operatingHours: {
    enabled: true,
    startTime: '09:00',
    endTime: '17:00'
  },
  resourceLimits: {
    cpuLimit: 80,
    memoryLimit: 1024
  }
})

const agentStats = ref({
  activeAgents: 0,
  totalTasks: 0,
  dailyTasks: 0,
  efficiencyScore: 0,
  responseRate: 0,
  efficiencyImprovement: 0,
  costSavings: 0,
  monthlySavings: 0
})

// Computed
const filteredAgents = computed(() => {
  if (!statusFilter.value) return aiAgents.value
  return aiAgents.value.filter(agent => agent.status === statusFilter.value)
})

// Methods
const initializeAgents = () => {
  aiAgents.value = [
    {
      id: '1',
      name: 'Job Matcher Pro',
      description: 'Advanced AI that matches oil field workers with suitable positions based on skills, experience, location, and preferences.',
      category: 'job-matching',
      status: 'active',
      tasksCompleted: 15420,
      successRate: 94,
      efficiency: 97,
      capabilities: ['Skill Matching', 'Location Analysis', 'Salary Optimization', 'Experience Validation'],
      created_at: '2024-01-15T10:30:00Z',
      updated_at: '2024-01-20T14:45:00Z'
    },
    {
      id: '2',
      name: 'Safety Compliance Guardian',
      description: 'Monitors safety protocols, tracks compliance requirements, and provides real-time safety guidance for oil field operations.',
      category: 'safety-compliance',
      status: 'active',
      tasksCompleted: 8930,
      successRate: 98,
      efficiency: 95,
      capabilities: ['Compliance Monitoring', 'Risk Assessment', 'Safety Training', 'Incident Prevention'],
      created_at: '2024-01-10T08:15:00Z',
      updated_at: '2024-01-18T16:20:00Z'
    },
    {
      id: '3',
      name: 'Schedule Optimizer',
      description: 'Optimizes crew schedules, prevents conflicts, and ensures optimal resource allocation across multiple oil field sites.',
      category: 'schedule-optimization',
      status: 'active',
      tasksCompleted: 6750,
      successRate: 89,
      efficiency: 92,
      capabilities: ['Resource Planning', 'Conflict Resolution', 'Shift Optimization', 'Workload Balancing'],
      created_at: '2024-01-08T14:20:00Z',
      updated_at: '2024-01-19T11:10:00Z'
    },
    {
      id: '4',
      name: 'Document Intelligence',
      description: 'Processes and extracts information from oil field documents, contracts, and technical specifications using advanced NLP.',
      category: 'document-processing',
      status: 'training',
      tasksCompleted: 2340,
      successRate: 85,
      efficiency: 78,
      capabilities: ['Document Parsing', 'Data Extraction', 'Contract Analysis', 'Technical Review'],
      created_at: '2024-01-05T12:00:00Z',
      updated_at: '2024-01-15T09:30:00Z'
    },
    {
      id: '5',
      name: 'Performance Analytics',
      description: 'Analyzes worker performance, productivity metrics, and provides insights for operational improvements.',
      category: 'data-analysis',
      status: 'active',
      tasksCompleted: 4580,
      successRate: 91,
      efficiency: 88,
      capabilities: ['Performance Tracking', 'Productivity Analysis', 'Trend Identification', 'Report Generation'],
      created_at: '2024-01-12T16:45:00Z',
      updated_at: '2024-01-22T10:30:00Z'
    },
    {
      id: '6',
      name: 'Communication Hub',
      description: 'Facilitates communication between workers, supervisors, and management with intelligent routing and priority handling.',
      category: 'communication',
      status: 'inactive',
      tasksCompleted: 1250,
      successRate: 82,
      efficiency: 75,
      capabilities: ['Message Routing', 'Priority Management', 'Multi-Channel Support', 'Alert System'],
      created_at: '2024-01-01T09:00:00Z',
      updated_at: '2024-01-10T15:20:00Z'
    }
  ]

  // Calculate stats
  const activeAgents = aiAgents.value.filter(agent => agent.status === 'active').length
  const totalTasks = aiAgents.value.reduce((sum, agent) => sum + agent.tasksCompleted, 0)
  const avgEfficiency = Math.round(aiAgents.value.reduce((sum, agent) => sum + agent.efficiency, 0) / aiAgents.value.length)
  const avgSuccessRate = Math.round(aiAgents.value.reduce((sum, agent) => sum + agent.successRate, 0) / aiAgents.value.length)

  agentStats.value = {
    activeAgents,
    totalTasks,
    dailyTasks: Math.round(totalTasks * 0.023), // ~2.3% of total as daily
    efficiencyScore: avgEfficiency,
    responseRate: avgSuccessRate,
    efficiencyImprovement: Math.floor(Math.random() * 12) + 3, // Random 3-14% improvement
    costSavings: Math.round(totalTasks * 2.5), // $2.50 per task savings
    monthlySavings: Math.round(totalTasks * 0.15) // 15% of total as monthly
  }
}

const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'active':
      return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
    case 'inactive':
      return 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
    case 'training':
      return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
    default:
      return 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
  }
}

const getAgentIconClass = (category: string) => {
  const baseClass = 'w-12 h-12 rounded-xl flex items-center justify-center'
  switch (category) {
    case 'job-matching':
      return `${baseClass} bg-gradient-to-br from-emerald-500 to-cyan-500`
    case 'safety-compliance':
      return `${baseClass} bg-gradient-to-br from-red-500 to-pink-500`
    case 'schedule-optimization':
      return `${baseClass} bg-gradient-to-br from-blue-500 to-indigo-500`
    case 'document-processing':
      return `${baseClass} bg-gradient-to-br from-purple-500 to-violet-500`
    case 'data-analysis':
      return `${baseClass} bg-gradient-to-br from-yellow-500 to-orange-500`
    case 'communication':
      return `${baseClass} bg-gradient-to-br from-emerald-500 to-cyan-500`
    default:
      return `${baseClass} bg-gradient-to-br from-slate-500 to-slate-600`
  }
}

const getAgentIcon = (category: string) => {
  const icons = {
    'job-matching': () => h('svg', { class: 'w-6 h-6', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6' })
    ]),
    'safety-compliance': () => h('svg', { class: 'w-6 h-6', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' })
    ]),
    'schedule-optimization': () => h('svg', { class: 'w-6 h-6', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' })
    ]),
    'document-processing': () => h('svg', { class: 'w-6 h-6', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' })
    ]),
    'data-analysis': () => h('svg', { class: 'w-6 h-6', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' })
    ]),
    'communication': () => h('svg', { class: 'w-6 h-6', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' })
    ])
  }
  return icons[category] || icons['job-matching']
}

const createNewAgent = () => {
  showCreateModal.value = true
}

const closeCreateModal = () => {
  showCreateModal.value = false
  agentForm.value = {
    name: '',
    category: '',
    description: '',
    capabilities: []
  }
  newCapability.value = ''
}

const closeConfigModal = () => {
  showConfigModal.value = false
  configuringAgent.value = null
}

const saveAgentConfiguration = () => {
  if (configuringAgent.value) {
    // In a real app, this would save the configuration to the backend
    console.log('Saving configuration for agent:', configuringAgent.value.name, agentConfig.value)
    
    // Update agent's updated_at timestamp
    configuringAgent.value.updated_at = new Date().toISOString()
    
    closeConfigModal()
  }
}

const addCapability = () => {
  if (newCapability.value.trim() && !agentForm.value.capabilities.includes(newCapability.value.trim())) {
    agentForm.value.capabilities.push(newCapability.value.trim())
    newCapability.value = ''
  }
}

const removeCapability = (index: number) => {
  agentForm.value.capabilities.splice(index, 1)
}

const saveAgent = () => {
  const newAgent: AIAgent = {
    id: Date.now().toString(),
    name: agentForm.value.name,
    category: agentForm.value.category,
    description: agentForm.value.description,
    status: 'active',
    tasksCompleted: 0,
    successRate: 0,
    efficiency: 0,
    capabilities: [...agentForm.value.capabilities],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
  
  aiAgents.value.push(newAgent)
  closeCreateModal()
  
  // Recalculate stats
  initializeAgents()
}

const toggleAgent = (agentId: string) => {
  const agent = aiAgents.value.find(a => a.id === agentId)
  if (agent) {
    agent.status = agent.status === 'active' ? 'inactive' : 'active'
    agent.updated_at = new Date().toISOString()
  }
}

const configureAgent = (agentId: string) => {
  const agent = aiAgents.value.find(a => a.id === agentId)
  if (agent) {
    configuringAgent.value = agent
    // Load existing configuration or set defaults
    loadAgentConfiguration(agent)
    showConfigModal.value = true
  }
}

const loadAgentConfiguration = (agent: AIAgent) => {
  // In a real app, this would load from agent's stored config
  // For now, set defaults based on agent category
  const categoryDefaults = {
    'job-matching': {
      maxTasksPerHour: 150,
      priorityLevel: 'high' as const,
      autoRetry: true,
      retryAttempts: 5
    },
    'safety-compliance': {
      maxTasksPerHour: 200,
      priorityLevel: 'critical' as const,
      autoRetry: true,
      retryAttempts: 3
    },
    'schedule-optimization': {
      maxTasksPerHour: 80,
      priorityLevel: 'medium' as const,
      autoRetry: true,
      retryAttempts: 4
    },
    'document-processing': {
      maxTasksPerHour: 60,
      priorityLevel: 'low' as const,
      autoRetry: false,
      retryAttempts: 2
    },
    'data-analysis': {
      maxTasksPerHour: 40,
      priorityLevel: 'medium' as const,
      autoRetry: true,
      retryAttempts: 3
    },
    'communication': {
      maxTasksPerHour: 300,
      priorityLevel: 'high' as const,
      autoRetry: true,
      retryAttempts: 2
    }
  }

  const defaults = categoryDefaults[agent.category] || categoryDefaults['job-matching']
  
  agentConfig.value = {
    ...agentConfig.value,
    ...defaults
  }
}

const viewAgentStats = (agentId: string) => {
  // Placeholder for agent stats
  alert(`Agent statistics view coming soon! Agent ID: ${agentId}`)
}

const viewAnalytics = () => {
  alert('Analytics dashboard coming soon!')
}

const configureSettings = () => {
  alert('Global settings configuration coming soon!')
}

const viewLogs = () => {
  alert('Activity logs viewer coming soon!')
}

// Lifecycle
onMounted(() => {
  initializeAgents()
})
</script>

<style scoped>
/* Admin Card Styles */
.admin-card {
  @apply bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-3xl overflow-hidden hover:border-emerald-500/20 transition-all duration-500;
}

.admin-card-header {
  @apply px-6 py-4 border-b border-slate-700/50 bg-slate-800/20;
}

.admin-card-title {
  @apply text-xl font-bold text-white mb-1;
}

.admin-card-subtitle {
  @apply text-sm text-slate-400;
}

.admin-card-body {
  @apply p-6;
}

/* Stat Cards */
.admin-stat-card {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-6 hover:border-emerald-500/30 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-emerald-500/10 h-full;
}

/* Quick Action Cards */
.quick-action-card {
  @apply bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-4 hover:border-emerald-500/30 transition-all duration-300 text-left;
}

.quick-action-icon {
  @apply w-10 h-10 rounded-xl flex items-center justify-center text-white mb-3;
}

/* Agent Cards */
.agent-card {
  @apply bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden hover:border-emerald-500/30 transition-all duration-300;
}

.agent-header {
  @apply p-6 border-b border-slate-700/50;
}

.agent-icon {
  @apply w-12 h-12 rounded-xl flex items-center justify-center;
}

.agent-name {
  @apply text-lg font-medium text-white mb-1;
}

.agent-category {
  @apply text-sm text-slate-400 capitalize;
}

.agent-body {
  @apply p-6 space-y-4;
}

.agent-description {
  @apply text-sm text-slate-300;
}

.agent-metrics {
  @apply grid grid-cols-3 gap-4;
}

.metric-item {
  @apply text-center;
}

.metric-label {
  @apply block text-xs text-slate-400 mb-1;
}

.metric-value {
  @apply block text-sm font-medium text-white;
}

.agent-capabilities {
  @apply space-y-2;
}

.capabilities-title {
  @apply text-xs font-medium text-slate-400 uppercase tracking-wider;
}

.capabilities-list {
  @apply flex flex-wrap gap-1;
}

.capability-tag {
  @apply px-2 py-1 bg-emerald-500/10 text-emerald-400 text-xs rounded-full border border-emerald-500/20;
}

.agent-actions {
  @apply flex items-center space-x-2;
}

/* Form Styles - Moved to Configuration section for better organization */

.capabilities-input {
  @apply space-y-3;
}

.capabilities-tags {
  @apply flex flex-wrap gap-2;
}

.capability-tag-editable {
  @apply px-3 py-1 bg-emerald-500/10 text-emerald-400 text-sm rounded-full border border-emerald-500/20 flex items-center space-x-2;
}

.capability-remove {
  @apply text-emerald-400 hover:text-red-400 font-bold text-lg leading-none;
}

/* Modal Styles */
.modal-overlay {
  @apply fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50;
}

.modal-content {
  @apply bg-slate-800/90 backdrop-blur-sm border border-slate-700/50 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden;
}

.modal-large {
  @apply max-w-4xl;
}

.modal-header {
  @apply flex items-center justify-between p-6 border-b border-slate-700/50;
}

.modal-title {
  @apply text-lg font-medium text-white;
}

.modal-close {
  @apply text-slate-400 hover:text-slate-300 transition-colors;
}

.modal-body {
  @apply p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)];
}

.modal-actions {
  @apply flex items-center justify-end space-x-3 pt-4 border-t border-slate-700/50;
}

.empty-state {
  @apply text-center py-16;
}

/* Configuration Form Styles */
.config-sections {
  @apply space-y-8;
}

.config-section {
  @apply bg-slate-700/20 border border-slate-600/30 rounded-2xl p-6;
}

.config-section-title {
  @apply text-lg font-semibold text-white mb-6 pb-3 border-b border-slate-600/30;
}

.form-group {
  @apply mb-6;
}

.form-label {
  @apply block text-sm font-medium text-slate-300 mb-3;
}

.form-input {
  @apply w-full bg-slate-700/50 border border-slate-600/50 text-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all duration-200;
}

.form-select {
  @apply w-full bg-slate-700/50 border border-slate-600/50 text-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all duration-200;
}

.form-hint {
  @apply text-xs text-slate-400 mt-3 block;
}

.form-checkbox {
  @apply mb-6 block;
}

.form-checkbox label {
  @apply flex items-center space-x-3 cursor-pointer;
}

.form-checkbox input[type="checkbox"] {
  @apply w-4 h-4 bg-slate-700/50 border border-slate-600/50 rounded focus:ring-2 focus:ring-emerald-500/50 text-emerald-500 flex-shrink-0;
}

.checkbox-label {
  @apply text-sm font-medium text-slate-300 whitespace-nowrap;
}

.form-grid {
  @apply grid grid-cols-1 md:grid-cols-2 gap-8;
}

/* Button Styles */
.btn-hero-primary {
  @apply px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 shadow-lg shadow-emerald-500/25 inline-flex items-center;
}

.btn-primary {
  @apply inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-lg hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300;
}

.btn-secondary {
  @apply inline-flex items-center px-4 py-2 bg-slate-700/50 text-slate-300 font-medium rounded-lg hover:bg-slate-600/50 transition-colors duration-200;
}

.btn-success {
  @apply inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-400 to-cyan-400 text-slate-900 font-semibold rounded-lg hover:from-emerald-500 hover:to-cyan-500 transition-all duration-300;
}

.btn-warning {
  @apply inline-flex items-center px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-semibold rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-all duration-300;
}

.btn-sm {
  @apply px-3 py-1.5 text-sm;
}
</style>